import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { GoogleMap, GoogleMapsModule, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { Store } from '@ngrx/store';
import { DictionaryNgrxModule } from '@teamfoster/sdk/dictionary-ngrx';
import { catchError, map, of } from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { SetCanonical, SetTheme } from 'src/app/store';
import { getOrderedThemes } from 'src/app/themes/store';
import { environment } from 'src/environments/environment';
import { StoriesService } from '../../services';
import { toSignal } from '@angular/core/rxjs-interop';
import { Story } from '../../models';
import { MapViewToggleComponent } from 'src/app/standalone/map-view-toggle/map-view-toggle.component';
import { LegColorOptions, OverlappingMarkerSpiderfier, SpiderOptions } from 'i-hate-spiders';
import { MapMarkerSpiderifierComponent } from 'src/app/standalone/components/map-marker-spiderifier/map-marker-spiderifier.component';
import { CustomMapMarkerDirective } from 'src/app/standalone/components/custom-map-marker/custom-map-marker.component';

@Component({
  selector: 'app-stories-map',
  templateUrl: './stories-map.component.html',
  standalone: true,
  imports: [
    CoreModule,
    DictionaryNgrxModule,
    GoogleMapsModule,
    CommonModule,
    MapViewToggleComponent,
    MapMarkerSpiderifierComponent,
    CustomMapMarkerDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StoriesMapComponent implements OnInit {
  private store = inject(Store);
  private httpClient = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private srv = inject(StoriesService);

  markerClustererImagePath = '/assets/gfx/map/cluster-size-';

  @ViewChild(MapInfoWindow) infoWindow?: MapInfoWindow;

  themes = this.store.selectSignal(getOrderedThemes);
  stories = toSignal(this.srv.getMapStories());
  selectedStory = signal<Story | null>(null);

  apiLoaded = of(false);

  initialCenter = { lat: 52.2739187, lng: 5.969158 };
  initialZoom = 9;

  mapStyle: google.maps.MapTypeStyle[] = [
    {
      featureType: 'poi',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'transit',
      elementType: 'labels.icon',
      stylers: [{ visibility: 'off' }],
    },
  ];

  options: google.maps.MapOptions = {
    center: this.initialCenter,
    zoom: this.initialZoom,
    disableDefaultUI: true,
    zoomControl: true,
    clickableIcons: false,
    styles: this.mapStyle,
  };

  spiderOptions: SpiderOptions = {
    markersWontMove: true,
    markersWontHide: true,
    basicFormatEvents: false,
    keepSpiderfied: true,
    circleFootSeparation: 40,
  };

  legColors: LegColorOptions = {
    highlighted: {
      ['hybrid']: '#56f5ee',
      ['roadmap']: '#56f5ee',
    },
  };

  infoWindowOptions: google.maps.InfoWindowOptions = {
    headerDisabled: true,
    maxWidth: 300,
  };

  mapMoved = false;

  cd = inject(ChangeDetectorRef);

  @ViewChild('maps') maps?: GoogleMap;

  ngOnInit() {
    this.store.dispatch(SetTheme({ theme: 'palette-dark' }));
    this.store.dispatch(SetCanonical({ url: 'https://onzeijssel.nl/verhalen/kaart' }));
    this.initMaps();
  }

  openInfoWindow(marker: CustomMapMarkerDirective, story: Story | null) {
    this.selectedStory.set(story);
    this.infoWindow?.open(marker);
    this.cd.detectChanges();
  }

  initMaps() {
    if (isPlatformBrowser(this.platformId)) {
      if (window['google']?.['maps']) {
        this.apiLoaded = of(true);
      } else {
        this.apiLoaded = this.httpClient
          .jsonp(`https://maps.googleapis.com/maps/api/js?key=${environment.mapsApiKey}&region=NL&language=NL`, 'callback')
          .pipe(
            map(() => true),
            catchError(e => {
              console.error('Google map not loaded', e);
              return of(false);
            })
          );
      }
    }
  }

  recenterMap() {
    if (this.maps) {
      //this.maps.panTo(this.initialCenter);

      // animate to initial zoom and center
      //this.maps.panTo(this.initialCenter);
      this.maps.googleMap?.setZoom(this.initialZoom);
      this.maps.googleMap?.setCenter(this.initialCenter);
    }
  }

  checkMapUpdate() {
    // check if current zoom and center is different from initial
    if (this.maps?.googleMap) {
      const center = this.maps.googleMap.getCenter();
      const zoom = this.maps.googleMap.getZoom();

      if (center?.lat() !== this.initialCenter.lat || center.lng() !== this.initialCenter.lng || zoom !== this.initialZoom) {
        this.mapMoved = true;
      } else {
        this.mapMoved = false;
      }
    }
  }

  markerIcon(story: Story): google.maps.Icon {
    const iconName = story.themes?.[0]?.icon || 'empty';
    return {
      url: `/assets/gfx/map/marker${iconName ? '-' + iconName : ''}.svg`,
      scaledSize: new google.maps.Size(40, 40),
    };
  }
}
